import { useCallback, useEffect } from 'react';
import { atom, useAtom } from 'jotai';
import { useUserLoggedIn } from '~/modules/user';
import type { Conversation } from '../types';

/**
 * The maximum number of conversations to cache. Make sure this is always larger
 * than the maximum number of open chat boxes.
 */
const MAX_CACHED_CONVERSATIONS = 20;

/**
 * The list of conversations that have been cached, ordered from newest to oldest.
 */
const conversationCacheAtom = atom<Conversation[]>([]);

/**
 * Hook to manage the conversation cache.
 */
export function useConversationCache() {
    const { isLoggedIn } = useUserLoggedIn();
    const [conversationCache, setConversationCache] = useAtom(
        conversationCacheAtom
    );

    const getCachedConversation = useCallback(
        (conversationID: number) => {
            return conversationCache.find(
                (c) => c.ID === String(conversationID)
            );
        },
        [conversationCache]
    );

    const getCachedConversationByParticipants = useCallback(
        (userID: number, memberIDs: number[]) => {
            if (memberIDs.length === 0) {
                return undefined;
            }
            return conversationCache.find(
                (c) =>
                    c.Participants.length === memberIDs.length + 1 &&
                    c.Participants.some(
                        ({ Identity }) =>
                            Identity.__typename === 'Member' &&
                            Identity.ID === userID
                    ) &&
                    memberIDs.every((memberID) =>
                        c.Participants.some(
                            ({ Identity }) =>
                                Identity.__typename === 'Member' &&
                                Identity.ID === memberID
                        )
                    )
            );
        },
        [conversationCache]
    );

    const cacheConversation = useCallback(
        (conversation: Conversation) => {
            setConversationCache((prev) => {
                return [
                    conversation,
                    ...prev.filter((c) => c.ID !== String(conversation.ID)),
                ].slice(0, MAX_CACHED_CONVERSATIONS);
            });
        },
        [setConversationCache]
    );

    const invalidateCachedConversation = useCallback(
        (conversationID: number) => {
            setConversationCache((prev) => {
                // Conditionally set new value to avoid unnecessary re-renders.
                if (prev.some((c) => c.ID === String(conversationID))) {
                    return prev.filter((c) => c.ID !== String(conversationID));
                }
                return prev;
            });
        },
        [setConversationCache]
    );

    // Clear the conversation cache when the user logs out.
    useEffect(() => {
        if (!isLoggedIn) {
            setConversationCache([]);
        }
    }, [isLoggedIn, setConversationCache]);

    return {
        /**
         * Get the cached conversation by the conversation ID.
         */
        getCachedConversation,
        /**
         * Get the cached conversation by the participants.
         */
        getCachedConversationByParticipants,
        /**
         * Cache a conversation. If the number of cached conversations exceeds the
         * maximum, the oldest conversation will be removed.
         */
        cacheConversation,
        /**
         * Invalidate a cached conversation.
         */
        invalidateCachedConversation,
    };
}
